import { CommonModule } from '@angular/common';
import { Component, OnInit, Signal, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { LocalUserRoles, PlaceTypeEnum } from '@buspro/shared/common';
import { AddStandardPlaceComponent, ProvisionResourcesModalComponent } from '@buspro/web/admin/shared';
import { RegisterTenantModalComponent } from '@buspro/web/admin/tenants/shared';
import { LocalUserDto } from '@buspro/web/apis';
import { PusherClientService } from '@buspro/web/core';
import { NgxAuthService } from '@corelabs/ng-auth';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { GeofenceConflictsWidgetComponent } from './components/geofence-conflicts-widget.component';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';
import { AdminDashboardApiService, InventoryStats } from './services/admin-dashboard-api.service';
import { AdminDashboardWebsocketService } from './services/admin-websocket.service';

interface DashboardStats {
  tenants: {
    total: number;
    active: number;
    provisioning: number;
    suspended: number;
    terminated: number;
  };
  buses: {
    total: number;
    active: number;
    maintenance: number;
    offline: number;
  };
  operations: {
    activeTrips: number;
    completedToday: number;
    revenue: number;
    yesterdayRevenue: number;
    revenueGrowth: number;
  };
  completedTrips: {
    today: number;
    yesterday: number;
    growth: number;
  };
  systemHealth: {
    status: 'healthy' | 'degraded' | 'down';
    uptime: number;
    apiStatus: string;
    dbStatus: string;
  };
  averageAuditDuration: number;
  inventory?: InventoryStats;
}

@UntilDestroy()
@Component({
  selector: 'buspro-admin-dashboard',
  standalone: true,
  providers: [],
  host: {
    class: 'w-full',
  },
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    NzButtonModule,
    NzCardModule,
    NzStatisticModule,
    NzGridModule,
    NzIconModule,
    NzBadgeModule,
    NzTagModule,
    NzSpinModule,
    NzAlertModule,
    RegisterTenantModalComponent,
    ProvisionResourcesModalComponent,
    AddStandardPlaceComponent,
    GeofenceConflictsWidgetComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  private apiService = inject(AdminDashboardApiService);
  private websocketService = inject(AdminDashboardWebsocketService);
  private logger = inject(NGXLogger);
  private queryClient = injectQueryClient();
  private router = inject(Router);
  private ngxAuthService = inject(NgxAuthService);
  private pusher = inject(PusherClientService);

  // Convert Observable to Signal for easier access
  authUser: Signal<LocalUserDto | null> = toSignal(this.ngxAuthService.state, { initialValue: null });

  // WebSocket connection status
  wsConnected = signal(false);

  // Quick Actions modal states
  showRegisterTenantModal = signal(false);
  showProvisionResourcesModal = signal(false);
  showAddPlaceModal = signal(false);

  // Place type enum for template
  readonly PlaceTypeEnum = PlaceTypeEnum;

  // Real-time tenant presence data from Pusher (converted from Observable to Signal)
  tenantPresenceData = toSignal(this.pusher.tenantPresence, { initialValue: [] });

  // Platform overview query
  platformOverviewQuery = injectQuery(() => ({
    queryKey: ['admin', 'dashboard', 'platform-overview'],
    queryFn: () => {
      this.logger.debug('Fetching platform overview stats...');
      return lastValueFrom(this.apiService.getPlatformOverviewStats()).then(
        (data) => {
          this.logger.debug('Platform overview stats received:', data);
          return data;
        },
        (error) => {
          this.logger.error('Error fetching platform overview stats:', error);
          throw error;
        },
      );
    },
    refetchOnWindowFocus: false,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 60000, // Keep in cache for 1 minute
    retry: 2, // Retry failed requests twice
  }));

  // Inventory stats query
  inventoryStatsQuery = injectQuery(() => ({
    queryKey: ['admin', 'dashboard', 'inventory-stats'],
    queryFn: () => {
      this.logger.debug('Fetching inventory stats...');
      return lastValueFrom(this.apiService.getInventoryStats()).then(
        (data) => {
          this.logger.debug('Inventory stats received:', data);
          return data;
        },
        (error) => {
          this.logger.error('Error fetching inventory stats:', error);
          throw error;
        },
      );
    },
    refetchOnWindowFocus: false,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 60000, // Keep in cache for 1 minute
    retry: 2,
  }));

  // Computed loading state
  isLoading = computed(() => this.platformOverviewQuery.isLoading() || this.platformOverviewQuery.isFetching());

  // Real-time online tenant users statistics computed from Pusher presence data
  onlineTenantUsersStats = computed(() => {
    const presenceData = this.tenantPresenceData();

    // Filter out admin observers (users with isObserver flag)
    // and calculate total tenant users only
    let totalUsers = 0;
    const filteredTenants = presenceData
      .map((tenant) => {
        const tenantUsers = tenant.onlineUsers.filter((user) => {
          // Filter out admin observers who are monitoring the channel
          return !(user.info as any).isObserver;
        });
        totalUsers += tenantUsers.length;
        return { ...tenant, onlineUsers: tenantUsers };
      })
      .filter((tenant) => tenant.onlineUsers.length > 0); // Only count tenants with actual users

    // Tenant count is the number of tenants with at least one non-observer user online
    const tenantCount = filteredTenants.length;

    this.logger.debug(`[Real-time] ${totalUsers} tenant users online across ${tenantCount} tenant(s)`);

    return {
      tenantCount,
      totalUsers,
      isLoading: false, // No loading state for real-time data
    };
  });

  // Optional: Keep REST API as fallback (disabled by default)
  // This can be enabled if Pusher connection fails
  onlineTenantUsersQuery = injectQuery(() => ({
    queryKey: ['admin', 'dashboard', 'online-tenant-users'],
    queryFn: () => {
      this.logger.debug('Fetching online tenant users stats from REST API (fallback)...');
      return lastValueFrom(this.apiService.getOnlineTenantUsersStats()).then(
        (data) => {
          this.logger.debug('Online tenant users stats received from REST API:', data);
          return data;
        },
        (error) => {
          this.logger.error('Error fetching online tenant users stats from REST API:', error);
          throw error;
        },
      );
    },
    refetchOnWindowFocus: false,
    enabled: false, // Disabled - using real-time Pusher data instead
    refetchInterval: 30000, // Reduced to 30 seconds as fallback only
    staleTime: 20000,
    gcTime: 60000,
    retry: 2,
  }));

  // Map PlatformOverviewStats to DashboardStats for template compatibility
  dashboardStats = computed<DashboardStats | null>(() => {
    const queryData = this.platformOverviewQuery.data();
    const queryError = this.platformOverviewQuery.error();
    const queryStatus = this.platformOverviewQuery.status();

    this.logger.debug('Dashboard stats computed - Query status:', queryStatus);
    this.logger.debug('Dashboard stats computed - Query data:', queryData);
    if (queryError) {
      this.logger.error('Dashboard stats computed - Query error:', queryError);
    }

    if (!queryData) {
      this.logger.debug('Dashboard stats: No data available yet');
      return null;
    }

    // Ensure all required fields exist with proper defaults
    const stats = {
      tenants: {
        total: queryData.tenants?.total ?? 0,
        active: queryData.tenants?.active ?? 0,
        provisioning: queryData.tenants?.provisioning ?? 0,
        suspended: queryData.tenants?.suspended ?? 0,
        terminated: queryData.tenants?.terminated ?? 0,
      },
      buses: {
        total: queryData.buses?.total ?? 0,
        active: queryData.buses?.active ?? 0,
        maintenance: queryData.buses?.maintenance ?? 0,
        offline: queryData.buses?.offline ?? 0,
      },
      operations: {
        activeTrips: queryData.activeTrips ?? 0,
        completedToday: queryData.completedTrips?.today ?? 0,
        revenue: queryData.todayRevenue ?? 0,
        yesterdayRevenue: queryData.yesterdayRevenue ?? 0,
        revenueGrowth: queryData.revenueGrowth ?? 0,
      },
      completedTrips: {
        today: queryData.completedTrips?.today ?? 0,
        yesterday: queryData.completedTrips?.yesterday ?? 0,
        growth: queryData.completedTrips?.growth ?? 0,
      },
      systemHealth: queryData.systemHealth ?? {
        status: 'degraded',
        uptime: 0,
        apiStatus: 'unknown',
        dbStatus: 'unknown',
      },
      averageAuditDuration: queryData.averageAuditDuration ?? 0,
      inventory: this.inventoryStatsQuery.data() ?? undefined,
    };

    this.logger.debug('Mapped dashboard stats:', stats);
    return stats;
  });

  // Widget visibility
  widgetVisibility = signal({
    platformOverview: true,
    tenantHealth: true,
    inventory: true,
    provisioning: true,
    activityFeed: true,
    alerts: true,
    revenue: true,
    map: true,
    quickActions: true,
    performance: true,
  });

  ngOnInit() {
    // Check if user should be redirected to their dedicated dashboard
    // Main Dashboard is only for LOCAL_ADMIN, LOCAL_MANAGER, and LOCAL_DEVELOPER
    const currentUser = this.authUser();
    if (currentUser) {
      const role = currentUser.role;
      if (role === LocalUserRoles.BUSPRO_BUS_STOP_MANAGER || role === LocalUserRoles.BUSPRO_BUS_STOP_CLERK) {
        this.logger.info('Bus stop staff detected, redirecting to bus stop dashboard');
        this.router.navigateByUrl('/bus-stop-dashboard');
        return;
      }
      if (role === LocalUserRoles.LOCAL_AUDITOR) {
        this.logger.info('Auditor detected, redirecting to auditors dashboard');
        this.router.navigateByUrl('/auditors-dashboard');
        return;
      }
      if (role === LocalUserRoles.LOCAL_TECHNICIAN) {
        this.logger.info('Technician detected, redirecting to technicians dashboard');
        this.router.navigateByUrl('/technicians-dashboard');
        return;
      }
    }

    this.setupWebSocketListeners();
    // Request initial data via WebSocket
    this.websocketService.emit();

    // Subscribe to Pusher presence channel for real-time tenant user tracking
    this.subscribeToTenantPresence();

    this.logger.debug('[Dashboard] Dashboard initialized - tenant users stats will be real-time from Pusher');
  }

  private subscribeToTenantPresence() {
    // Helper function to subscribe to presence channel
    const subscribeToPresence = () => {
      this.logger.log('[Admin Dashboard] Pusher connection ready, subscribing to tenant presence channel');

      // Subscribe to presence channel for all online tenant users
      this.pusher.subscribeToPresenceChannel('online-users');

      this.logger.log('[Admin Dashboard] Successfully subscribed to presence-online-users channel');
      this.logger.log('[Admin Dashboard] Real-time tenant presence tracking enabled');
    };

    // If already connected, subscribe immediately
    if (this.pusher.connectionState.value === 'connected') {
      subscribeToPresence();
    } else {
      // Wait for Pusher to be connected before subscribing to presence channel
      this.pusher.connectionState.pipe(untilDestroyed(this)).subscribe((state) => {
        if (state === 'connected') {
          this.logger.log('[Admin Dashboard] Pusher connected, subscribing to presence channel');
          subscribeToPresence();
        } else if (state === 'disconnected' || state === 'failed') {
          this.logger.warn(`[Admin Dashboard] Pusher connection ${state}`);
        }
      });
    }
  }

  private setupWebSocketListeners() {
    // Listen for platform overview updates
    this.websocketService
      .listen()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => {
          this.logger.debug('Received platform overview update via WebSocket:', data);
          // Update query cache with new data
          this.queryClient.setQueryData(['admin', 'dashboard', 'platform-overview'], data);
          this.wsConnected.set(true);
        },
        error: (error) => {
          this.logger.error('WebSocket error:', error);
          this.wsConnected.set(false);
        },
      });

    // Listen for completed trips updates
    this.websocketService
      .listenCompletedTrips()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => {
          this.logger.debug('Received completed trips update:', data);
          const currentData = this.platformOverviewQuery.data();
          if (currentData) {
            this.queryClient.setQueryData(['admin', 'dashboard', 'platform-overview'], {
              ...currentData,
              completedTrips: data,
            });
          }
        },
        error: (error) => {
          this.logger.error('WebSocket completed trips error:', error);
        },
      });
  }

  refreshDashboard() {
    this.platformOverviewQuery.refetch();
    this.websocketService.emit(); // Request fresh data via WebSocket
  }

  exportDashboard() {
    this.logger.debug('Exporting dashboard data...');
    // TODO: Implement export functionality
  }

  get healthStatusColor(): string {
    const stats = this.dashboardStats();
    if (!stats) return 'default';
    const status = stats.systemHealth.status;
    switch (status) {
      case 'healthy':
        return 'success';
      case 'degraded':
        return 'warning';
      case 'down':
        return 'error';
      default:
        return 'default';
    }
  }

  get healthStatusText(): string {
    const stats = this.dashboardStats();
    if (!stats) return 'Unknown';
    const status = stats.systemHealth.status;
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  getErrorMessage(): string {
    const error = this.platformOverviewQuery.error();
    if (error && typeof error === 'object' && 'message' in error) {
      return error.message as string;
    }
    return 'Please try refreshing the page';
  }

  formatAuditDuration(seconds: number): string {
    if (!seconds || seconds === 0) {
      return '0m';
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }

  formatUptime(seconds: number): string {
    if (!seconds || seconds === 0) {
      return '0s';
    }

    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }

  getStatusColor(status: string): string {
    switch (status?.toLowerCase()) {
      case 'up':
      case 'healthy':
      case 'operational':
        return 'success';
      case 'degraded':
        return 'warning';
      case 'down':
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  }

  getStatusIcon(status: string): string {
    switch (status?.toLowerCase()) {
      case 'up':
      case 'healthy':
      case 'operational':
        return 'check-circle';
      case 'degraded':
        return 'warning';
      case 'down':
      case 'error':
        return 'close-circle';
      default:
        return 'question-circle';
    }
  }

  // Quick Actions handlers
  onRegisterTenant() {
    this.showRegisterTenantModal.set(true);
  }

  onProvisionResources() {
    this.showProvisionResourcesModal.set(true);
  }

  onAddBusType() {
    this.router.navigate(['/bus-types']);
  }

  onAddPlace() {
    this.showAddPlaceModal.set(true);
  }

  onRegisterTenantModalClosed() {
    this.showRegisterTenantModal.set(false);
    // Optionally refresh dashboard data
    this.platformOverviewQuery.refetch();
  }

  onProvisionResourcesModalClosed() {
    this.showProvisionResourcesModal.set(false);
    // Optionally refresh dashboard data
    this.inventoryStatsQuery.refetch();
    this.platformOverviewQuery.refetch();
  }

  onAddPlaceModalClosed() {
    this.showAddPlaceModal.set(false);
  }
}
